import { Component, Inject, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Observable, Subscription } from "rxjs";
import { debounceTime, map, shareReplay, tap } from "rxjs/operators";
import {
    AircraftService,
    AssessmentService,
    StatsService,
    USER_IS_AT_LEAST_ADMIN,
    UserService,
} from "@/_services";
import { FilterService } from "@/_services/filter.service";
import {
    MitigationStats,
    STATS_FLIGHT_STATUS_MAP,
    statusesOfferedBy,
    StatsResult,
} from "@/_models";
import { UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import moment from "moment";
import { RadioOption } from "@/components/shared/radio-list/radio-list.component";
import { getTimeFrameList } from "@/_models/filters/default-time-frame-list";
import { LOCAL_STORAGE_KEYS } from "@/_models/filters/local-storage-keys";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import {
    buildStatsFilterDefaults,
    mergeFilterDefaults,
} from "@/_models/filters/shared-assessment-filters";
import { reapplyTimeFrameRange } from "@/_utility/date.utils";
import { GroupsService } from "@/_services/groups.service";
import {
    GroupFilterOptions,
    toStatsGroupParams,
    withoutGroupFilters,
} from "@/_models/filters/group-filter-options";
import { isEnabled } from "@features/functions";

@UntilDestroy()
@Component({
    selector: "app-assessment-statistics",
    templateUrl: "./assessment-statistics.component.html",
    styleUrls: ["./assessment-statistics.component.scss"],
})
export class AssessmentStatisticsComponent implements OnInit {
    stats$: Observable<StatsResult>;
    statsData$: Observable<MitigationStats>;
    assessmentId: string;
    assessmentTitle$: Observable<string>;
    filterForm: UntypedFormGroup;
    multipleStatsData$: Observable<MitigationStats[]>;
    isSingleStatMode = false;
    currentFilters = null;

    statusList = STATS_FLIGHT_STATUS_MAP;

    timeFrameList: RadioOption[] = getTimeFrameList(["All"]);

    userList$: Observable<RadioOption[]>;
    aircraftList$: Observable<RadioOption[]>;

    groupsEnabled = isEnabled("groupsLocations");
    groupFilterOptions$: Observable<GroupFilterOptions>;

    private filterFormSubscription: Subscription;

    constructor(
        private route: ActivatedRoute,
        private assessmentService: AssessmentService,
        private filterService: FilterService,
        private userService: UserService,
        private statsService: StatsService,
        private aircraftService: AircraftService,
        private groupsService: GroupsService,
        private formBuilder: UntypedFormBuilder,
        @Inject(USER_IS_AT_LEAST_ADMIN) public isAdmin$: Observable<boolean>,
    ) {}

    ngOnInit(): void {
        this.assessmentId = this.route.parent?.snapshot.params["assessmentId"];

        if (this.assessmentId) {
            this.loadStats();
            this.loadAssessmentTitle();
            this.loadFilterData();
        }
    }

    loadStats() {
        const today = moment().format("YYYY-MM-DD");
        const oneYearAgo = moment().subtract(365, "days").format("YYYY-MM-DD");

        const localStorageKey = LOCAL_STORAGE_KEYS.ASSESSMENT_FILTERS(
            this.assessmentId,
        );

        const defaults = buildStatsFilterDefaults(this.assessmentId, [
            oneYearAgo,
            today,
        ]);
        const persisted = this.filterService.loadSimpleFilters(
            localStorageKey,
            {},
        );
        const mergedFilters = mergeFilterDefaults(defaults, persisted);

        this.filterForm = this.formBuilder.group({
            // Shared localStorage key with the register, which offers
            // Triaged. StatsService cannot filter on it (#3035).
            status: [
                statusesOfferedBy(
                    mergedFilters.status,
                    STATS_FLIGHT_STATUS_MAP,
                ),
            ],
            showArchived: [mergedFilters.showArchived],
            responsiblePilot: [mergedFilters.responsiblePilot],
            timeFrame: [mergedFilters.timeFrame],
            flightDate: [mergedFilters.flightDate],
            aircraft: [mergedFilters.aircraft],
            airport: [mergedFilters.airport],
            assessment: [mergedFilters.assessment],
            formType: [mergedFilters.formType],
        });

        if (this.groupsEnabled) {
            this.filterForm.addControl("groups", this.formBuilder.control([]));
        }

        reapplyTimeFrameRange(this.filterForm, "flightDate");

        this.filterFormSubscription = this.filterForm.valueChanges
            .pipe(
                untilDestroyed(this),
                debounceTime(500),
                tap((changes) => {
                    this.currentFilters = { ...changes };

                    this.filterService.saveSimpleFilters(
                        localStorageKey,
                        withoutGroupFilters(changes),
                    );

                    this.updateFlightList();
                }),
            )
            .subscribe();

        this.filterForm.updateValueAndValidity({ emitEvent: true });

        this.currentFilters = { ...this.filterForm.value };

        this.filterService.saveSimpleFilters(
            localStorageKey,
            withoutGroupFilters(this.currentFilters),
        );
    }

    updateFlightList() {
        this.stats$ = this.statsService
            .getStats(toStatsGroupParams(this.filterForm.value))
            .pipe(shareReplay());

        this.statsData$ = this.stats$.pipe(
            map((stats) => {
                const result = Array.isArray(stats.data) ? null : stats.data;
                this.isSingleStatMode = !!result;
                return result;
            }),
            shareReplay(),
        );

        this.multipleStatsData$ = this.stats$.pipe(
            map((stats) => {
                const result = Array.isArray(stats.data) ? stats.data : null;
                return result;
            }),
            shareReplay(),
        );
    }

    loadAssessmentTitle() {
        this.assessmentTitle$ = this.assessmentService
            .getAssessment(this.assessmentId)
            .pipe(map((assessment) => assessment?.title || "Assessment"));
    }

    private loadFilterData() {
        if (this.groupsEnabled) {
            this.groupFilterOptions$ = this.groupsService
                .getGroupFilterOptions()
                .pipe(shareReplay());
        }

        this.userList$ = this.userService.getUsers().pipe(
            map((users) =>
                users.map((user) => {
                    return { label: user.fullName, value: user._id };
                }),
            ),
            shareReplay(),
        );

        this.aircraftList$ = this.aircraftService.listAircraft().pipe(
            map((aircrafts) =>
                aircrafts.map((aircraft) => {
                    return { label: aircraft.designation, value: aircraft._id };
                }),
            ),
            shareReplay(),
        );
    }
}
